import { gsap } from "../animations/gsapSetup.ts";
import { SeoHead } from "../components/common/SeoHead.tsx";
import { useGsapScrollTrigger } from "../hooks/useGsapScrollTrigger.ts";
import { useReducedMotion } from "../hooks/useReducedMotion.ts";
import { useI18n } from "../i18n/context.tsx";
import { LiveGameView } from "../sections/aiteam/LiveGameView.tsx";
import { LoadManagement } from "../sections/aiteam/LoadManagement.tsx";
import { ManagementDashboard } from "../sections/aiteam/ManagementDashboard.tsx";
import { RealtimeHeartRate } from "../sections/aiteam/RealtimeHeartRate.tsx";
import { RestSuggestion } from "../sections/aiteam/RestSuggestion.tsx";
import { TeamCalendar } from "../sections/aiteam/TeamCalendar.tsx";
import { TeamRoster } from "../sections/aiteam/TeamRoster.tsx";
import { getSeoMeta } from "../utils/seo.ts";
import styles from "./AiTeam.module.css";

/* Avatar positions for hero gather animation */
const AVATARS = [
  { id: "a1", x: -200, y: -150, delay: 0 },
  { id: "a2", x: 250, y: -120, delay: 0.1 },
  { id: "a3", x: -280, y: 60, delay: 0.2 },
  { id: "a4", x: 300, y: 80, delay: 0.15 },
  { id: "a5", x: -150, y: 200, delay: 0.25 },
  { id: "a6", x: 180, y: 220, delay: 0.3 },
  { id: "a7", x: -320, y: -60, delay: 0.12 },
  { id: "a8", x: 350, y: -40, delay: 0.22 },
];

export function AiTeamPage() {
  const { t, locale } = useI18n();
  const reducedMotion = useReducedMotion();

  /* Hero gather animation */
  const heroRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const avatars = el.querySelectorAll("[data-avatar]");
    const headline = el.querySelector("[data-hero-title]");
    const subtitle = el.querySelector("[data-hero-subtitle]");
    const ctas = el.querySelector("[data-hero-ctas]");

    // Avatars start scattered, gather toward center
    gsap.set(avatars, { opacity: 0, scale: 0.3 });
    const tl = gsap.timeline({ delay: 0.3 });

    let idx = 0;
    for (const av of avatars) {
      const avEl = av as HTMLElement;
      tl.to(
        avEl,
        {
          opacity: 0.7,
          scale: 1,
          x: 0,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
        },
        idx * 0.08,
      );
      idx++;
    }

    // Title + subtitle
    if (headline) {
      gsap.set(headline, { y: 40, opacity: 0 });
      tl.to(headline, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }, 0.5);
    }
    if (subtitle) {
      gsap.set(subtitle, { y: 30, opacity: 0 });
      tl.to(subtitle, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }, 0.7);
    }
    if (ctas) {
      gsap.set(ctas, { y: 20, opacity: 0 });
      tl.to(ctas, { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" }, 0.9);
    }
  }, []);

  /* Pain points stagger */
  const painRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const cards = el.querySelectorAll("[data-pain-card]");
    const solution = el.querySelector("[data-pain-solution]");
    gsap.set(cards, { y: 40, opacity: 0 });
    gsap.to(cards, {
      y: 0,
      opacity: 1,
      duration: 0.7,
      stagger: 0.15,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
    if (solution) {
      gsap.set(solution, { y: 20, opacity: 0 });
      gsap.to(solution, {
        y: 0,
        opacity: 1,
        duration: 0.6,
        delay: 0.5,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      });
    }
  }, []);

  /* Dashboard counter animation */
  const dashRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const counters = el.querySelectorAll("[data-counter]");
    for (const counter of counters) {
      const target = Number((counter as HTMLElement).dataset.counter) || 0;
      const obj = { val: 0 };
      gsap.to(obj, {
        val: target,
        duration: 1.5,
        ease: "power2.out",
        snap: { val: 1 },
        onUpdate: () => {
          counter.textContent = String(Math.round(obj.val));
        },
        scrollTrigger: {
          trigger: el,
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      });
    }

    const bars = el.querySelectorAll("[data-bar]");
    for (const bar of bars) {
      const target = (bar as HTMLElement).dataset.bar || "0";
      gsap.set(bar, { width: "0%" });
      gsap.to(bar, {
        width: `${target}%`,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      });
    }
  }, []);

  /* Models scroll animation */
  const modelsRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const cards = el.querySelectorAll("[data-model-card]");
    gsap.set(cards, { y: 50, opacity: 0, scale: 0.9 });
    gsap.to(cards, {
      y: 0,
      opacity: 1,
      scale: 1,
      duration: 0.8,
      stagger: 0.2,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  /* Features grid stagger */
  const featRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const cards = el.querySelectorAll("[data-feat-card]");
    gsap.set(cards, { y: 40, opacity: 0 });
    gsap.to(cards, {
      y: 0,
      opacity: 1,
      duration: 0.6,
      stagger: 0.1,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  /* Pricing cards */
  const priceRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const cards = el.querySelectorAll("[data-plan-card]");
    gsap.set(cards, { y: 50, opacity: 0, scale: 0.9 });
    gsap.to(cards, {
      y: 0,
      opacity: 1,
      scale: 1,
      duration: 0.7,
      stagger: 0.15,
      ease: "back.out(1.2)",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  return (
    <>
      <SeoHead
        title={getSeoMeta("aiteam", locale).title}
        description={getSeoMeta("aiteam", locale).description}
        path="/basketball/aiteam"
      />

      {/* ═══════ 1. Hero ═══════ */}
      <section ref={heroRef} className={styles.hero}>
        <div className={styles.heroAvatars}>
          {AVATARS.map((av) => (
            <div
              key={av.id}
              className={styles.avatar}
              data-avatar
              style={
                {
                  "--start-x": `${av.x}px`,
                  "--start-y": `${av.y}px`,
                  transform: `translate(${av.x}px, ${av.y}px)`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle} data-hero-title>
            {t.aiTeam.heroTitle}
          </h1>
          <p className={styles.heroSubtitle} data-hero-subtitle>
            {t.aiTeam.heroSubtitle}
          </p>
          <div className={styles.heroCtas} data-hero-ctas>
            <button type="button" className={styles.ctaPrimary}>
              {t.aiTeam.heroCta}
            </button>
            <button type="button" className={styles.ctaSecondary}>
              {t.aiTeam.heroDemo}
            </button>
          </div>
        </div>
      </section>

      {/* ═══════ 2. Pain Points ═══════ */}
      <section ref={painRef} className={styles.painSection}>
        <h2 className={styles.sectionTitle}>{t.aiTeam.painTitle}</h2>
        <div className={styles.painCards}>
          {t.aiTeam.painCards.map((card) => (
            <div key={card.role} className={styles.painCard} data-pain-card>
              <span className={styles.painIcon}>{card.icon}</span>
              <span className={styles.painRole}>{card.role}</span>
              <p className={styles.painText}>{card.pain}</p>
            </div>
          ))}
        </div>
        <p className={styles.painSolution} data-pain-solution>
          {t.aiTeam.painSolution}
        </p>
      </section>

      {/* ═══════ 3. Team Roster ═══════ */}
      <TeamRoster />

      {/* ═══════ 4. Realtime Heart Rate + Polar BLE ═══════ */}
      <RealtimeHeartRate />

      {/* ═══════ 5. Live Game View ═══════ */}
      <LiveGameView />

      {/* ═══════ 6. Load Management ═══════ */}
      <LoadManagement />

      {/* ═══════ 7. Team Calendar ═══════ */}
      <TeamCalendar />

      {/* ═══════ 7.5. AI Rest Suggestions ═══════ */}
      <RestSuggestion />

      {/* ═══════ 8. Management Dashboard ═══════ */}
      <ManagementDashboard />

      {/* ═══════ 9. Dashboard Preview ═══════ */}
      <section ref={dashRef} className={styles.dashSection}>
        <h2 className={styles.sectionTitle}>{t.aiTeam.dashboardTitle}</h2>
        <p className={styles.sectionSubtitle}>{t.aiTeam.dashboardSubtitle}</p>
        <div className={styles.dashPreview}>
          <div className={styles.dashCard}>
            <span className={styles.dashCardLabel}>Team FG%</span>
            <span className={styles.dashCardBig} data-counter={58}>
              0
            </span>
            <span className={styles.dashCardUnit}>%</span>
          </div>
          <div className={styles.dashCard}>
            <span className={styles.dashCardLabel}>Sessions</span>
            <span className={styles.dashCardBig} data-counter={342}>
              0
            </span>
          </div>
          <div className={styles.dashCard}>
            <span className={styles.dashCardLabel}>Players</span>
            <span className={styles.dashCardBig} data-counter={24}>
              0
            </span>
          </div>
          <div className={styles.dashBars}>
            <div className={styles.dashBarRow}>
              <span className={styles.dashBarLabel}>Paint</span>
              <div className={styles.dashBarTrack}>
                <div
                  className={styles.dashBarFill}
                  data-bar={78}
                  style={{ background: "#ff9500" }}
                />
              </div>
              <span className={styles.dashBarPct}>78%</span>
            </div>
            <div className={styles.dashBarRow}>
              <span className={styles.dashBarLabel}>Mid</span>
              <div className={styles.dashBarTrack}>
                <div
                  className={styles.dashBarFill}
                  data-bar={55}
                  style={{ background: "#34c759" }}
                />
              </div>
              <span className={styles.dashBarPct}>55%</span>
            </div>
            <div className={styles.dashBarRow}>
              <span className={styles.dashBarLabel}>3PT</span>
              <div className={styles.dashBarTrack}>
                <div
                  className={styles.dashBarFill}
                  data-bar={34}
                  style={{ background: "#0071e3" }}
                />
              </div>
              <span className={styles.dashBarPct}>34%</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 10. Sharing Models ═══════ */}
      <section ref={modelsRef} className={styles.modelsSection}>
        <h2 className={styles.sectionTitle}>{t.aiTeam.modelsTitle}</h2>
        <div className={styles.modelCards}>
          {t.aiTeam.models.map((model) => (
            <div key={model.id} className={styles.modelCard} data-model-card>
              <span className={styles.modelIcon}>{model.icon}</span>
              <h3 className={styles.modelTitle}>{model.title}</h3>
              <p className={styles.modelDesc}>{model.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ 11. Features ═══════ */}
      <section ref={featRef} className={styles.featSection}>
        <h2 className={styles.sectionTitle}>{t.aiTeam.featuresTitle}</h2>
        <div className={styles.featGrid}>
          {t.aiTeam.features.map((feat) => (
            <div key={feat.title} className={styles.featCard} data-feat-card>
              <span className={styles.featIcon}>{feat.icon}</span>
              <h3 className={styles.featTitle}>{feat.title}</h3>
              <p className={styles.featDesc}>{feat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ 12. Pricing ═══════ */}
      <section ref={priceRef} className={styles.priceSection}>
        <h2 className={styles.sectionTitle}>{t.aiTeam.pricingTitle}</h2>
        <div className={styles.planCards}>
          {t.aiTeam.plans.map((plan) => (
            <div
              key={plan.name}
              className={`${styles.planCard} ${plan.recommended ? styles.planRecommended : ""}`}
              data-plan-card
            >
              {plan.recommended && <span className={styles.planBadge}>Recommended</span>}
              <h3 className={styles.planName}>{plan.name}</h3>
              <div className={styles.planPrice}>
                <span className={styles.planAmount}>{plan.price}</span>
                <span className={styles.planNote}>{plan.note}</span>
              </div>
              <span className={styles.planMembers}>{plan.members}</span>
              <ul className={styles.planFeatures}>
                {plan.features.map((f) => (
                  <li key={f} className={styles.planFeature}>
                    {f}
                  </li>
                ))}
              </ul>
              <button type="button" className={styles.planCta}>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ 13. Final CTA ═══════ */}
      <section className={styles.finalCta}>
        <h2 className={styles.finalCtaTitle}>{t.aiTeam.ctaTitle}</h2>
        <p className={styles.finalCtaSubtitle}>{t.aiTeam.ctaSubtitle}</p>
        <button type="button" className={styles.ctaPrimary}>
          {t.aiTeam.ctaButton}
        </button>
      </section>
    </>
  );
}
